import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ConfirmModal,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  Modal,
  buttonClassName,
} from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { extractApiError } from '../../lib/api';
import { companyMergeApi, type CompanyMergeRequestInfo } from '../../lib/company-merge';
import { directionParticleOf, topicParticleOf, withDirectionParticle } from '@safetyhub/shared';
import { MODAL_LAYER } from '../../components/modalLayers';

/**
 * 조직 통합 수락 화면 — 흡수 **대상** 조직의 소유자가 보는 자리.
 *
 * 공유 토큰 페이지가 아니다. 서버가 대상 조직의 활성 멤버만 열람을 허용하고(조직 규모가
 * 토큰만으로 새면 안 되므로) 수락·거절은 OWNER만 가능하다 — 로그인이 전제다.
 *
 * 수락하면 이 조직이 사라진다. 그래서 확인은 danger(취소가 기본 포커스)이고, 성공 후에는
 * 세션의 소속이 통째로 바뀌므로 `refreshSession(정본 id)`으로 다시 읽어 워크스페이스를 **정본
 * 조직으로** 옮긴다.
 *
 * ⚠️ 여기서 `fetchUser()`를 쓰면 안 된다. 두 가지가 동시에 깨진다:
 *  1) fetchUser는 전역 isLoading을 토글하고, App의 `{isLoading ? 로딩 : Routes}` 구조가 이
 *     페이지를 언마운트한다 → 완료 요약(`done`)이 사라지고 재진입해 재조회 →
 *     ALREADY_RESOLVED가 "통합 요청을 열 수 없습니다"라는 **오류 화면으로 성공을 덮는다**.
 *  2) fetchUser의 소속 폴백은 `memberships[0]`이라 정본이 아니라 아무 소속(개인 워크스페이스)
 *     으로 떨어진다.
 * 재조회가 완료 상태를 덮지 않도록 로더 자체도 처리 완료 후에는 돌지 않는다.
 */
export default function CompanyMergeAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, refreshSession } = useAuthStore();
  const { showToast } = useToastStore();

  const [info, setInfo] = useState<CompanyMergeRequestInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  /** 처리 완료 화면 — 수락(엔진 완료 여부 포함)과 거절을 한 자리에서 말한다. */
  const [done, setDone] = useState<{ title: string; message: string } | null>(null);
  /**
   * 여기서 처리를 끝냈는지. 끝낸 뒤의 재조회는 서버가 ALREADY_RESOLVED로 막는 게 정상이라,
   * 그 응답으로 완료 화면을 오류로 덮으면 사용자는 성공한 일을 실패로 읽는다.
   */
  const isResolvedRef = useRef(false);

  useEffect(() => {
    if (!token || !isAuthenticated || isResolvedRef.current) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const loadInfo = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const result = await companyMergeApi.getByToken(token);
        if (!cancelled) setInfo(result);
      } catch (err) {
        if (!cancelled) {
          setLoadError(extractApiError(err).message || '통합 요청 정보를 불러올 수 없습니다.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadInfo();
    return () => {
      cancelled = true;
    };
  }, [token, isAuthenticated]);

  const handleAccept = async () => {
    if (!token) return;
    setIsAccepting(true);
    try {
      const result = await companyMergeApi.accept(token);
      isResolvedRef.current = true;
      setIsConfirmOpen(false);
      // 엔진까지 끝났을 때만 "완료"다. 실패하면 수락 상태로 남아 운영자가 마저 처리하므로
      // 여기서 완료라고 말하면 사용자는 옮겨지지 않은 데이터를 찾아 헤매게 된다.
      setDone(
        result.executed
          ? {
              title: '통합이 완료되었습니다',
              message: `조직의 모든 데이터가 ${withDirectionParticle(info?.canonicalCompanyName ?? '요청 조직')} 옮겨졌습니다. 앞으로는 그 조직에서 이어서 작업하시면 됩니다.`,
            }
          : {
              title: '수락을 접수했습니다',
              message:
                '통합 처리가 아직 끝나지 않았습니다. 운영자가 확인 후 마무리하며, 완료되기 전까지는 기존 조직에서 그대로 작업할 수 있습니다.',
            },
      );
      // 소속이 통째로 바뀌었다 — 세션을 다시 읽고 정본 조직으로 옮긴다. 엔진이 아직 안 끝났으면
      // (executed=false) 우리 조직이 살아 있으므로 정본 소속이 없을 수 있고, 그때는
      // refreshSession이 머물던 조직을 그대로 유지한다.
      await refreshSession(result.canonicalCompanyId);
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    if (!token) return;
    setIsRejecting(true);
    try {
      await companyMergeApi.reject(token, rejectReason.trim() || undefined);
      isResolvedRef.current = true;
      setIsRejectOpen(false);
      setDone({
        title: '통합 요청을 거절했습니다',
        message: '조직은 그대로 유지됩니다. 요청한 조직에 거절 사실이 전달됩니다.',
      });
    } catch (err) {
      showToast(extractApiError(err).message, 'error');
    } finally {
      setIsRejecting(false);
    }
  };

  const shell = (children: ReactNode) => (
    <div className="min-h-screen bg-ink-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-card shadow-sm max-w-md w-full">{children}</div>
    </div>
  );

  if (!isAuthenticated) {
    return shell(
      <div className="p-8 text-center">
        <h2 className="text-lg font-semibold text-ink-900">조직 통합 요청</h2>
        <p className="mt-2 text-sm text-ink-500">
          요청 내용을 확인하려면 로그인이 필요합니다. 이 조직의 소유자 계정으로 로그인해 주세요.
        </p>
        <Link
          to={`/login?redirect=/company-merge/${token}`}
          className={buttonClassName('primary', 'md', 'mt-4 inline-flex')}
        >
          로그인하기
        </Link>
      </div>,
    );
  }

  if (isLoading) {
    return shell(
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto" />
        <p className="mt-3 text-sm text-ink-500">통합 요청을 불러오는 중...</p>
      </div>,
    );
  }

  if (done) {
    return shell(
      <div className="p-8 text-center">
        <h2 className="text-lg font-semibold text-ink-900">{done.title}</h2>
        <p className="mt-2 text-sm text-ink-600">{done.message}</p>
        <Button variant="primary" onClick={() => navigate('/')} className="mt-6">
          홈으로 이동
        </Button>
      </div>,
    );
  }

  if (loadError || !info) {
    return shell(
      <div className="p-8 text-center">
        <h2 className="text-lg font-semibold text-ink-900">통합 요청을 열 수 없습니다</h2>
        <p className="mt-2 text-sm text-ink-500">{loadError}</p>
        <p className="mt-1 text-xs text-ink-400">
          요청이 만료되었거나 이미 처리된 요청일 수 있습니다. 이 조직의 소유자만 열 수 있습니다.
        </p>
        <Link to="/" className="mt-6 inline-block text-sm text-brand-600 hover:text-brand-800">
          홈으로 이동
        </Link>
      </div>,
    );
  }

  return (
    <>
      {shell(
        <>
          <div className="bg-brand-600 px-6 py-5 text-white rounded-t-card">
            <h1 className="text-lg font-semibold">조직 통합 요청</h1>
            <p className="mt-1 text-sm text-brand-100">
              "{info.canonicalCompanyName}"에서 요청
              {info.requesterName ? ` (${info.requesterName})` : ''}
            </p>
          </div>

          <div className="p-6 space-y-5">
            <div className="rounded-card bg-ink-50 p-4 space-y-1">
              <p className="text-xs text-ink-500">통합될 조직 (우리)</p>
              <p className="text-sm font-semibold text-ink-900">{info.targetCompanyName}</p>
              <div className="flex gap-4 pt-2">
                <div>
                  <p className="text-xs text-ink-500">그룹</p>
                  <p className="text-sm font-medium text-ink-900">{info.targetGroupCount}개</p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">구성원</p>
                  <p className="text-sm font-medium text-ink-900">{info.targetMemberCount}명</p>
                </div>
                <div>
                  <p className="text-xs text-ink-500">현장</p>
                  <p className="text-sm font-medium text-ink-900">{info.targetSiteLinkCount}곳</p>
                </div>
              </div>
            </div>

            <div className="rounded-card border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 space-y-2">
              <p className="font-medium">수락하면 이렇게 됩니다</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>
                  위 그룹·구성원·현장과 점검·보고서가 모두 "{info.canonicalCompanyName}"
                  {directionParticleOf(info.canonicalCompanyName)} 옮겨집니다.
                </li>
                <li>
                  <strong>
                    이 조직 "{info.targetCompanyName}"{topicParticleOf(info.targetCompanyName)} 사라집니다.
                  </strong> 되돌릴 수
                  없습니다.
                </li>
                <li>이 조직의 구독은 해지됩니다.</li>
                <li>구성원 계정은 유지되며 소속만 옮겨집니다.</li>
              </ul>
            </div>

            <p className="text-xs text-ink-400">
              {new Date(info.expiresAt).toLocaleDateString('ko-KR')}까지 응답하지 않으면 요청은
              만료됩니다.
            </p>

            <div className="flex gap-3 pt-1">
              <Button
                variant="secondary"
                onClick={() => setIsRejectOpen(true)}
                disabled={isAccepting || isRejecting}
                className="flex-1"
              >
                거절
              </Button>
              <Button
                variant="primary"
                onClick={() => setIsConfirmOpen(true)}
                disabled={isAccepting || isRejecting}
                className="flex-1"
              >
                수락
              </Button>
            </div>
          </div>
        </>,
      )}

      <ConfirmModal
        open={isConfirmOpen}
        title="조직 통합을 수락합니다"
        body={
          <div className="space-y-2 text-sm text-ink-600">
            <p>
              수락하면 "{info.targetCompanyName}"의 모든 데이터가 "{info.canonicalCompanyName}"
              {directionParticleOf(info.canonicalCompanyName)} 옮겨집니다.
            </p>
            <p className="font-medium text-rose-600">
              이 조직은 사라집니다. 되돌릴 수 없습니다.
            </p>
          </div>
        }
        confirmLabel="수락하고 통합"
        danger
        busy={isAccepting}
        overlayZClassName={MODAL_LAYER.root}
        onConfirm={handleAccept}
        onClose={() => {
          if (!isAccepting) setIsConfirmOpen(false);
        }}
      />

      <Modal
        open={isRejectOpen}
        onClose={() => {
          if (!isRejecting) setIsRejectOpen(false);
        }}
        title="통합 요청 거절"
        size="sm"
        overlayZClassName={MODAL_LAYER.root}
        closeOnOverlay={!isRejecting}
        closeOnEsc={!isRejecting}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsRejectOpen(false)}
              disabled={isRejecting}
            >
              취소
            </Button>
            <Button type="button" variant="primary" onClick={handleReject} disabled={isRejecting}>
              {isRejecting ? '처리 중...' : '거절하기'}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-500">
            거절해도 조직은 그대로 유지됩니다. 사유를 적으면 요청한 조직과 운영자가 함께 보게
            됩니다.
          </p>
          <div>
            <label htmlFor="merge-reject-reason" className={FORM_LABEL_CLASS}>
              거절 사유 <span className="text-ink-400">(선택)</span>
            </label>
            <textarea
              id="merge-reject-reason"
              rows={3}
              maxLength={500}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              disabled={isRejecting}
              className={FORM_CONTROL_CLASS}
              placeholder="예: 별도 조직으로 계속 운영할 예정입니다."
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
